import { CheckCircleOutline, Close, CloudUploadOutlined, DeleteOutline, ErrorOutline, HourglassEmpty, Image } from '@mui/icons-material';
import { Avatar, Box, Button, ButtonBase, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, LinearProgress, List, ListItem, ListItemAvatar, ListItemAvatarProps, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { AppModels, brandNewFile, deleteModel, FileAttrs, fileSelector, fileSlice, hasErrors, MAX_UPLOADING_FILES, saveModel, selectActiveFileUploads, selecteRelatedImages, selectFilesWaitingForUpload, selectUploadingFile, UploadFileInfo, uploadManagerSlice, UploadResponse, UploadState } from 'cdx-common';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { appStore, RootState } from '../store';
import { FileLoader } from '../views/AppItems';
import { useToaster } from './Toaster';



export interface UploadQueue {
    file: File,
    id: string,
    base64Content: string | null;
}

export const uploadFileQueue = new Map<string, UploadQueue>();

export interface UploadFilesOptions {
    relation?: {
        type: AppModels,
        id: ModelId;
    },
    accept?: string[],
    multiple?: boolean;
}

export interface DroppableProps extends UploadFilesOptions {
    element: HTMLElement,
    onDragOver: (canDrop: boolean) => void,
    onDragOut: () => void,
    onDrop: (uploadIds: ModelId[]) => void;
}

const normalizeAccept = (accept: readonly string[] = []): string[] => accept
    .map(value => value.trim().toLocaleLowerCase())
    .filter(value => !!value)
    .map(value => value.startsWith('.') || value.includes('/') ? value : `.${value}`);

const fileMatchesAccept = (file: File, accept: readonly string[] = []): boolean => {
    const normalizedAccept = normalizeAccept(accept);
    if (!normalizedAccept.length) return true;

    const fileName = file.name.toLocaleLowerCase();
    const mimeType = file.type.toLocaleLowerCase();

    return normalizedAccept.some(value => {
        if (value.startsWith('.')) return fileName.endsWith(value);
        if (value.endsWith('/*')) return mimeType.startsWith(value.slice(0, -1));
        return mimeType === value;
    });
};

const mimeTypesByExtension: Record<string, readonly string[]> = {
    '.bmp': ['image/bmp'],
    '.csv': ['text/csv', 'application/csv'],
    '.doc': ['application/msword'],
    '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    '.gif': ['image/gif'],
    '.heic': ['image/heic'],
    '.heif': ['image/heif'],
    '.jpeg': ['image/jpeg'],
    '.jpg': ['image/jpeg'],
    '.json': ['application/json'],
    '.mp3': ['audio/mpeg'],
    '.mp4': ['video/mp4'],
    '.pdf': ['application/pdf'],
    '.png': ['image/png'],
    '.ppt': ['application/vnd.ms-powerpoint'],
    '.pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    '.svg': ['image/svg+xml'],
    '.txt': ['text/plain'],
    '.webp': ['image/webp'],
    '.xls': ['application/vnd.ms-excel'],
    '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    '.xml': ['application/xml', 'text/xml'],
    '.zip': ['application/zip', 'application/x-zip-compressed']
};

const dataTransferItemMatchesAccept = (item: DataTransferItem, accept: readonly string[] = []): boolean => {
    const normalizedAccept = normalizeAccept(accept);
    if (!normalizedAccept.length) return true;

    const mimeType = item.type.toLocaleLowerCase();

    // Certains navigateurs ne dévoilent même pas le type MIME avant le drop.
    // Dans ce cas, la validation exacte sera faite sur le File au moment du dépôt.
    if (!mimeType) return true;

    return normalizedAccept.some(value => {
        if (value.startsWith('.')) {
            const knownMimeTypes = mimeTypesByExtension[value];
            return knownMimeTypes ? knownMimeTypes.includes(mimeType) : true;
        }
        if (value.endsWith('/*')) return mimeType.startsWith(value.slice(0, -1));
        return mimeType === value;
    });
};

const canQueueFiles = (files: readonly File[], options: UploadFilesOptions): boolean => {
    if (!files.length) return false;
    if (options.multiple === false && files.length > 1) return false;
    return files.every(file => fileMatchesAccept(file, options.accept));
};

const canQueueDataTransfer = (dataTransfer: DataTransfer, options: UploadFilesOptions): boolean => {
    const fileItems = Array.from(dataTransfer.items).filter(item => item.kind === 'file');

    if (!fileItems.length) return false;
    if (options.multiple === false && fileItems.length > 1) return false;

    return fileItems.every(item => dataTransferItemMatchesAccept(item, options.accept));
};



export interface UploadManagerInterface {
    /* upload a file, the return the new FillAttrs id */
    droppable: (props: DroppableProps) => void,
    undroppable: (element: HTMLElement) => void,
    upload: (file: File) => ModelId;
    queueFiles: (files: readonly File[], options: UploadFilesOptions) => ModelId[];
    openUploadDialog: () => void;

}

const UploadManagerContext = createContext({} as unknown as UploadManagerInterface);

export const UploadManager = (props: { children: React.ReactNode; }) => {

    const filesWaitingForUpload = useSelector((state: RootState) => selectFilesWaitingForUpload(state));
    const activeFileUploads = useSelector((state: RootState) => selectActiveFileUploads(state));
    const uploads = useSelector((state: RootState) => state.uploadManager.files);
    const currentUserId = useSelector((state: RootState) => state.session.userId);
    const [dropzones, setDropZones] = React.useState<DroppableProps[]>([]);
    const [currentlyDraggingOver, setcurrentlyDraggingOver] = useState<DroppableProps | undefined>();
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);


    const saveImagePreview = async (file: File, attrs: FileAttrs) => {
        if (attrs.isImage) {
            const fileReader = new FileReader();
            fileReader.onload = (fr) => {
                if (fr.loaded && typeof fileReader.result == 'string') {
                    if (attrs.uploadId) {
                        const p = uploadFileQueue.get(attrs.uploadId);
                        if (p) {
                            uploadFileQueue.set(attrs.uploadId, {
                                ...p,
                                base64Content: fileReader.result
                            });
                        }
                    }
                }
            };
            fileReader.readAsDataURL(file);
        }
    };


    const upload = (file: File, fileAttr?: Partial<FileAttrs>): ModelId => {
        const id: string = '~' + crypto.randomUUID();

        const uploadDataInfo: UploadFileInfo = {
            id,
            name: file.name,
            state: UploadState.NONE,
            type: file.type,
            loaded: 0,
            total: 0
        };

        const newFile: FileAttrs = {
            ...brandNewFile,
            ...fileAttr,
            authorId: currentUserId,
            name: file.name,
            mimetype: file.type,
            isImage: file.type.includes('image'),
            id
        };
        uploadFileQueue.set(id, {
            file,
            id,
            base64Content: null
        });

        appStore.dispatch(uploadManagerSlice.actions.set(uploadDataInfo));

        appStore.dispatch(fileSlice.actions.addOne(newFile));

        // set preview content (is image)
        if (newFile.isImage) {
            void saveImagePreview(file, newFile);
        }

        return newFile.id;
    };

    const queueFiles = (files: readonly File[], options: UploadFilesOptions): ModelId[] => {
        if (!canQueueFiles(files, options)) return [];

        return files.map(file => upload(file, options.relation ? {
            relatedType: options.relation.type,
            relatedId: options.relation.id
        } : undefined));
    };

    React.useEffect(() => {

        if (activeFileUploads.length < MAX_UPLOADING_FILES && filesWaitingForUpload.length) {
            appStore.dispatch(uploadManagerSlice.actions.update({
                id: filesWaitingForUpload[0].id,
                state: UploadState.QUEUED
            }));
        }
    }, [activeFileUploads, filesWaitingForUpload]);

    const findDropzone = (el: HTMLElement): DroppableProps | undefined => {
        return dropzones.find(p => p.element.contains(el));
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const found = findDropzone(event.target as HTMLElement);
        if (found) {
            const canDrop = canQueueDataTransfer(event.dataTransfer, found);
            event.dataTransfer.dropEffect = canDrop ? 'copy' : 'none';
            if (found != currentlyDraggingOver) {
                currentlyDraggingOver?.onDragOut();
                setcurrentlyDraggingOver(found);
            }
            found.onDragOver(canDrop);
        } else {
            event.dataTransfer.dropEffect = 'none';
            if (currentlyDraggingOver) {
                currentlyDraggingOver.onDragOut();
                setcurrentlyDraggingOver(undefined);
            }
        }
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const found = findDropzone(event.target as HTMLElement);
        if (found) {
            const files = Array.from(event.dataTransfer.files);
            const canDrop = canQueueFiles(files, found);
            found.onDragOut();
            setcurrentlyDraggingOver(undefined);

            if (canDrop) {
                const uploadIds = queueFiles(files, found);
                found.onDrop(uploadIds);
            }
        }

    };

    const droppable = (prop: DroppableProps) => {
        const curr = dropzones.find(p => p.element === prop.element);
        if (curr) {
            setDropZones(p => {
                return p.map(p => p.element === curr.element ? prop : p);
            });
        } else {
            setDropZones(p => p.concat([prop]));
        }
    };

    const undroppable = (element: HTMLElement) => {
        setDropZones(p => {
            return p.filter(p => p.element !== element);
        });
    };


    return <UploadManagerContext.Provider value={{
        upload,
        queueFiles,
        droppable,
        undroppable,
        openUploadDialog: () => { setIsUploadDialogOpen(true); }
    }}><>
            <div
                onDragOver={onDragOver}
                onDrop={onDrop}>
                {props.children}
            </div>
            {activeFileUploads.map(p => <FileUploder key={p.id} uploadId={p.id} />)}
            <UploadIndicator uploads={uploads} onClick={() => { setIsUploadDialogOpen(true); }} />
            <UploadDialog
                open={isUploadDialogOpen}
                uploads={uploads}
                onClose={() => { setIsUploadDialogOpen(false); }}
            />
        </></UploadManagerContext.Provider >;

};

const isPendingUpload = (upload: UploadFileInfo) => upload.state === UploadState.NONE
    || upload.state === UploadState.QUEUED
    || upload.state === UploadState.PAUSED
    || upload.state === UploadState.UPLOADING;

const UploadIndicator = (props: {
    uploads: UploadFileInfo[];
    onClick: () => void;
}) => {
    const pendingCount = props.uploads.filter(isPendingUpload).length;
    const errorCount = props.uploads.filter(upload => upload.state === UploadState.ERROR).length;

    if (!pendingCount && !errorCount) return null;

    return <Paper
        elevation={4}
        sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: theme => theme.zIndex.snackbar,
            overflow: 'hidden'
        }}
    >
        <ButtonBase
            onClick={props.onClick}
            aria-label="Afficher les téléversements"
            sx={{ minHeight: 44, px: 1.5, py: 1, textAlign: 'left' }}
        >
            <Stack direction="row" alignItems="center" spacing={1.25}>
                {pendingCount > 0 && <>
                    <CircularProgress size={20} thickness={5} />
                    <Typography variant="body2">
                        {pendingCount} fichier{pendingCount > 1 ? 's' : ''} en cours
                    </Typography>
                </>}
                {errorCount > 0 && <Stack direction="row" alignItems="center" spacing={0.5} color="error.main">
                    <ErrorOutline fontSize="small" />
                    <Typography variant="body2" color="inherit">
                        {errorCount} erreur{errorCount > 1 ? 's' : ''}
                    </Typography>
                </Stack>}
            </Stack>
        </ButtonBase>
    </Paper>;
};

const uploadStateLabel = (state: UploadState): string => {
    switch (state) {
        case UploadState.NONE: return 'En attente';
        case UploadState.QUEUED: return 'Dans la file';
        case UploadState.PAUSED: return 'En pause';
        case UploadState.UPLOADING: return 'Téléversement en cours';
        case UploadState.UPLOADED: return 'Terminé';
        case UploadState.ERROR: return 'Erreur';
        default: return state;
    }
};

const UploadStateIcon = (props: { state: UploadState; }) => {
    switch (props.state) {
        case UploadState.UPLOADED:
            return <CheckCircleOutline color="success" />;
        case UploadState.ERROR:
            return <ErrorOutline color="error" />;
        case UploadState.UPLOADING:
            return <CircularProgress size={22} />;
        default:
            return <HourglassEmpty color="action" />;
    }
};

const UploadDialog = (props: {
    open: boolean;
    uploads: UploadFileInfo[];
    onClose: () => void;
}) => <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadOutlined />
            <Typography component="span" variant="h6" sx={{ flex: 1 }}>Téléversements</Typography>
            <IconButton onClick={props.onClose} aria-label="Fermer">
                <Close />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
            {props.uploads.length === 0
                ? <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    Aucun téléversement.
                </Typography>
                : <List disablePadding>
                    {props.uploads.map((upload, index) => <React.Fragment key={upload.id}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40, pt: 0.25 }}>
                                <UploadStateIcon state={upload.state} />
                            </ListItemIcon>
                            <ListItemText
                                primary={upload.name}
                                secondary={<Stack spacing={0.75} component="span" sx={{ mt: 0.5 }}>
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        color={upload.state === UploadState.ERROR ? 'error.main' : 'text.secondary'}
                                    >
                                        {uploadStateLabel(upload.state)}
                                    </Typography>
                                    {upload.state === UploadState.UPLOADING && <Box component="span">
                                        <LinearProgress
                                            variant={upload.total > 0 ? 'determinate' : 'indeterminate'}
                                            value={upload.total > 0
                                                ? Math.min(100, (upload.loaded / upload.total) * 100)
                                                : undefined}
                                        />
                                    </Box>}
                                    {upload.error && <Typography component="span" variant="body2" color="error.main">
                                        {upload.error}
                                    </Typography>}
                                </Stack>}
                                primaryTypographyProps={{ noWrap: true }}
                            />
                        </ListItem>
                    </React.Fragment>)}
                </List>}
        </DialogContent>
    </Dialog>;

const FileUploder = (props: {
    uploadId: string;
}) => {

    const uploadInfo = useSelector((state: RootState) => selectUploadingFile(state, props.uploadId));
    const [file, setFile] = React.useState<File | undefined>();
    const fileModel = useSelector((state: RootState) => fileSelector.selectById(state, props.uploadId));


    React.useEffect(() => {
        if (!uploadInfo) {
            const err = 'Cant upload file. File upload information not found';
            appStore.dispatch(uploadManagerSlice.actions.update({
                id: props.uploadId,
                state: UploadState.ERROR,
                error: err
            }));
            console.error(err);
        } else if (!fileModel) {
            const err = 'Cant upload file. File Model not found';
            appStore.dispatch(uploadManagerSlice.actions.update({
                id: props.uploadId,
                state: UploadState.ERROR,
                error: err
            }));
            console.error(err);
        } else {
            const p = uploadFileQueue.get(props.uploadId);
            if (p) {
                setFile(p.file);
            } else {
                const err = 'Cant upload file. File upload queue information not found';
                appStore.dispatch(uploadManagerSlice.actions.update({
                    id: props.uploadId,
                    state: UploadState.ERROR,
                    error: err
                }));
                console.error(err);
            }
        }
    }, []);

    React.useEffect(() => {
        if (file && uploadInfo?.state === UploadState.QUEUED) {
            void startUpoading();
        }
    }, [file, uploadInfo]);

    const startUpoading = async () => {

        if (file && fileModel && uploadInfo && uploadInfo?.state === UploadState.QUEUED) {

            const data = new FormData();

            data.append('file', file);

            appStore.dispatch(uploadManagerSlice.actions.update({
                id: uploadInfo.id,
                state: UploadState.UPLOADING,
                error: undefined
            }));


            try {
                const url: string = './file/up';


                const xhr = new XMLHttpRequest();
                const response: UploadResponse = await new Promise((resolve) => {
                    xhr.upload.addEventListener("progress", (event) => {
                        if (event.lengthComputable) {
                            appStore.dispatch(uploadManagerSlice.actions.update({
                                id: uploadInfo.id,
                                loaded: event.loaded,
                                total: event.total
                            }));
                        }
                    });
                    xhr.addEventListener("progress", (event) => {
                        if (event.lengthComputable) {
                            appStore.dispatch(uploadManagerSlice.actions.update({
                                id: uploadInfo.id,
                                loaded: event.loaded,
                                total: event.total
                            }));
                        }
                    });
                    xhr.addEventListener("loadend", () => {
                        resolve(xhr.readyState === 4 && xhr.status === 200 ? JSON.parse(xhr.response) : 'Erreur code ' + xhr.status);
                    });
                    xhr.open("POST", url, true);
                    xhr.send(data);
                });

                if (typeof response == 'string') {

                    appStore.dispatch(uploadManagerSlice.actions.update({
                        id: uploadInfo.id,
                        state: UploadState.ERROR,
                        error: response,
                    }));

                } else {

                    const updatedFileModel = {
                        ...fileModel,
                        ...response,
                        uploadId: null
                    };
                    appStore.dispatch(fileSlice.actions.updateOne({
                        id: fileModel.id,
                        changes: {
                            ...updatedFileModel
                        }
                    }));
                    const savedModel = await saveModel(AppModels.files, updatedFileModel);
                    if (hasErrors(savedModel)) {
                        throw new Error("Le fichier téléversé n'a pas pu être sauvegardé");
                    }
                    appStore.dispatch(fileSlice.actions.updateOne({ id: updatedFileModel.id, changes: { ...savedModel } }));
                    uploadFileQueue.delete(uploadInfo.id);
                    appStore.dispatch(uploadManagerSlice.actions.update({
                        id: uploadInfo.id,
                        fileId: savedModel.id,
                        state: UploadState.UPLOADED,
                        error: undefined,
                        fileInfo: {
                            ...response,
                            uploadId: uploadInfo.id
                        }
                    }));
                }
            } catch (error) {
                appStore.dispatch(uploadManagerSlice.actions.update({
                    id: uploadInfo.id,
                    state: UploadState.ERROR,
                    error: error instanceof Error ? error.message : String(error)
                }));
            }
        }
    };
    return <></>;
};

const relatedFileCache: Set<string> = new Set;

export const UpdatableImage = (props: {
    relation?: {
        type: AppModels,
        id: ModelId;
    },
    value: ModelId | null,
    canUpload: boolean,
    onUpdated: (file: FileAttrs) => void,
    onRemoved?: () => void;
}) => {
    const { droppable, undroppable, queueFiles } = useUploadManager();
    const { toast } = useToaster();
    const [activeUploadId, setActiveUploadId] = useState<ModelId>();
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string>();
    const [isOver, setIsOver] = useState(false);
    const [canDrop, setCanDrop] = useState(false);
    const [removing, setRemoving] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const localPreviewUrlRef = useRef<string | undefined>(undefined);
    const notifiedUploadIdRef = useRef<ModelId | undefined>(undefined);
    const uploadInfo = useSelector((state: RootState) => selectUploadingFile(state, activeUploadId));
    const uploadedFile = useSelector((state: RootState) => uploadInfo?.fileId
        ? fileSelector.selectById(state, uploadInfo.fileId)
        : undefined);
    const valueFile = useSelector((state: RootState) => props.value
        ? fileSelector.selectById(state, props.value)
        : undefined);
    const currentFile = uploadedFile ?? valueFile;
    const uploadInProgress = !!uploadInfo && isPendingUpload(uploadInfo);
    const imageAccept = 'image/*';

    const setPreview = (file?: File) => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
            localPreviewUrlRef.current = undefined;
        }

        const previewUrl = file ? URL.createObjectURL(file) : undefined;
        localPreviewUrlRef.current = previewUrl;
        setLocalPreviewUrl(previewUrl);
    };

    const trackUpload = (uploadIds: ModelId[]) => {
        const uploadId = uploadIds[0];
        if (!uploadId) return;

        notifiedUploadIdRef.current = undefined;
        setActiveUploadId(uploadId);
        setPreview(uploadFileQueue.get(uploadId)?.file);
    };

    useEffect(() => () => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
        }
    }, []);

    useEffect(() => {
        if (!props.canUpload || uploadInProgress) return;

        const element = boxRef.current;
        if (!element) return;

        droppable({
            relation: props.relation,
            accept: [imageAccept],
            multiple: false,
            element,
            onDragOver: accepted => {
                setIsOver(true);
                setCanDrop(accepted);
            },
            onDragOut: () => {
                setIsOver(false);
                setCanDrop(false);
            },
            onDrop: uploadIds => {
                setIsOver(false);
                setCanDrop(false);
                trackUpload(uploadIds);
            }
        });

        return () => {
            undroppable(element);
        };
    }, [props.canUpload, props.relation?.type, props.relation?.id, uploadInProgress]);

    useEffect(() => {
        if (uploadInfo?.state !== UploadState.UPLOADED
            || !uploadedFile
            || notifiedUploadIdRef.current === uploadInfo.id) return;

        notifiedUploadIdRef.current = uploadInfo.id;
        setPreview();
        props.onUpdated(uploadedFile);
        setActiveUploadId(undefined);
    }, [uploadInfo, uploadedFile, props.onUpdated]);

    const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.currentTarget.files?.[0];
        if (selectedFile) {
            trackUpload(queueFiles([selectedFile], {
                relation: props.relation,
                accept: [imageAccept],
                multiple: false
            }));
        }
        event.currentTarget.value = '';
    };

    const onRemove = async () => {
        if (!currentFile || removing || uploadInProgress) return;
        if (!window.confirm(`Supprimer l'image « ${currentFile.name} » ?`)) return;

        setRemoving(true);
        try {
            const removed = await deleteModel(AppModels.files, currentFile.id);
            if (removed) {
                appStore.dispatch(fileSlice.actions.removeOne(currentFile.id));
                setActiveUploadId(undefined);
                setPreview();
                props.onRemoved?.();
                toast.success("L'image a été supprimée");
            } else {
                toast.error("L'image n'a pas pu être supprimée");
            }
        } catch (_error) {
            toast.error("L'image n'a pas pu être supprimée");
        } finally {
            setRemoving(false);
        }
    };

    const previewUrl = localPreviewUrl || currentFile?.thumbUrl || currentFile?.url;
    const progress = uploadInfo && uploadInfo.total > 0
        ? Math.min(100, (uploadInfo.loaded / uploadInfo.total) * 100)
        : undefined;

    return <>
        <FileLoader id={props.value} />
        <Paper
            ref={boxRef}
            variant="outlined"
            sx={{
                p: 1,
                borderStyle: 'dashed',
                borderWidth: isOver ? 2 : 1,
                borderColor: isOver ? (canDrop ? 'primary.main' : 'error.main') : 'divider',
                cursor: isOver ? (canDrop ? 'copy' : 'not-allowed') : undefined
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                    src={previewUrl}
                    alt={currentFile?.name ?? 'Aperçu de l’image'}
                    variant="rounded"
                    sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: 'action.hover' }}
                >
                    {props.value && !valueFile && !uploadedFile && !localPreviewUrl
                        ? <CircularProgress size={20} />
                        : <Image fontSize="small" />}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {props.canUpload && <>
                        <input
                            ref={fileInputRef}
                            hidden
                            type="file"
                            accept={imageAccept}
                            capture="environment"
                            onChange={onFileSelected}
                        />
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            disabled={uploadInProgress}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Modifier
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Glissez l’image ici pour mettre à jour
                        </Typography>
                    </>}
                    {uploadInProgress && <Box sx={{ mt: 1 }}>
                        <LinearProgress
                            variant={progress === undefined ? 'indeterminate' : 'determinate'}
                            value={progress}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {progress === undefined ? uploadStateLabel(uploadInfo.state) : `Téléversement : ${Math.round(progress)} %`}
                        </Typography>
                    </Box>}
                    {uploadInfo?.state === UploadState.ERROR && <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                        {uploadInfo.error ?? 'Le téléversement a échoué'}
                    </Typography>}
                </Box>
                {!!props.onRemoved && !!currentFile?.url && <IconButton
                    type="button"
                    color="error"
                    disabled={removing || uploadInProgress}
                    aria-label="Supprimer l’image"
                    title="Supprimer"
                    onClick={onRemove}
                    sx={{ flexShrink: 0 }}
                >
                    {removing ? <CircularProgress size={20} /> : <DeleteOutline />}
                </IconButton>}
            </Stack>
        </Paper>
    </>;
};

export const UploadableFileList = (props: {
    accept: string[],
    relation: {
        type: AppModels,
        id: ModelId;
    },
    canUpload: boolean,
    canRemove?: boolean;
}) => {

    const files = useSelector((state: RootState) => selecteRelatedImages(state, props.relation.type, props.relation.id));
    const { droppable, undroppable, queueFiles } = useUploadManager();
    const [isOver, setIsOver] = useState(false);
    const [canDrop, setCanDrop] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const accept = normalizeAccept(props.accept).join(',');

    React.useEffect(() => {
        if (!relatedFileCache.has(props.relation.type + '-' + props.relation.id)) {
            // fetch file list !
        }
    }, []);

    const boxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!props.canUpload) return;
        const ref = boxRef.current;
        if (ref) {
            droppable({
                relation: props.relation,
                accept: props.accept,
                multiple: true,
                element: ref,
                onDragOver: canDrop => {
                    setIsOver(true);
                    setCanDrop(canDrop);
                },
                onDragOut: () => {
                    setIsOver(false);
                    setCanDrop(false);
                },
                onDrop: () => { }
            });
        }

        return () => {
            if (ref) {
                undroppable(ref);
            }
        };
    }, [props.canUpload, props.relation.type, props.relation.id, accept]);

    const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.currentTarget.files ?? []);
        queueFiles(selectedFiles, {
            relation: props.relation,
            accept: props.accept,
            multiple: true
        });

        event.currentTarget.value = '';
    };

    return <div ref={boxRef} style={props.canUpload ? {
        borderWidth: '5px',
        borderStyle: 'dotted',
        borderColor: isOver ? (canDrop ? '#3e48d4' : '#d32f2f') : '#e4e5f3',
        cursor: isOver ? (canDrop ? 'copy' : 'not-allowed') : undefined
    } : undefined}>
        <List>
            {files.map(file => <FileListItem key={file.id} file={file} canRemove={props.canRemove === true} />)}
        </List>
        {props.canUpload && <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}>
            <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={accept || undefined}
                multiple
                onChange={onFilesSelected}
            />
            <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<CloudUploadOutlined />}
                onClick={() => fileInputRef.current?.click()}
            >
                Ajouter
            </Button>
        </Box>}
    </div>;
};

const FileListItem = (props: { file: FileAttrs; canRemove: boolean; }) => {
    const { toast } = useToaster();
    const [removing, setRemoving] = useState(false);

    const onRemove = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (removing || !window.confirm(`Supprimer le fichier « ${props.file.name} » ?`)) return;

        setRemoving(true);
        try {
            const removed = await deleteModel(AppModels.files, props.file.id);
            if (removed) {
                appStore.dispatch(fileSlice.actions.removeOne(props.file.id));
                toast.success('Le fichier a été supprimé');
            } else {
                toast.error("Le fichier n'a pas pu être supprimé");
            }
        } catch (_error) {
            toast.error("Le fichier n'a pas pu être supprimé");
        } finally {
            setRemoving(false);
        }
    };

    return <ListItem disablePadding>
        <ListItemButton href={props.file.url} target='_blank' sx={{ minWidth: 0 }}>
            <FileListItemAvatar file={props.file} />
            <ListItemText primary={props.file.name} primaryTypographyProps={{ noWrap: true }} />
        </ListItemButton>
        {props.canRemove && <IconButton
            color="error"
            disabled={removing}
            aria-label={`Supprimer ${props.file.name}`}
            title="Supprimer"
            onClick={onRemove}
            sx={{ mx: 1, flexShrink: 0 }}
        >
            {removing ? <CircularProgress size={20} /> : <DeleteOutline />}
        </IconButton>}
    </ListItem>;
};

const FileListItemAvatar = (props: ListItemAvatarProps & { file: FileAttrs; }) => {
    if (props.file.isImage) {
        return <ListItemAvatar {...props}><Avatar variant="square" src={props.file.url} /></ListItemAvatar>;
    } else {
        return <ListItemAvatar {...props}><Avatar variant="square"><Image /></Avatar></ListItemAvatar>;
    }
};

export const useUploadManager = () => useContext(UploadManagerContext);
